// Grouped-bar activity chart, shared verbatim between payhook-dashboard and
// payhook-console. Keep the two copies identical so a fix in one lands in both.

// Wide ranges (1y, lifetime) would otherwise draw one bar per day; bucket
// down to a legible bar count by summing consecutive days together.
export function resampleSeries (series, maxPoints = 60) {
  if (!series?.length || series.length <= maxPoints) return series || []
  const bucketSize = Math.ceil(series.length / maxPoints)
  const buckets = []
  for (let i = 0; i < series.length; i += bucketSize) {
    const chunk = series.slice(i, i + bucketSize)
    const bucket = { date: chunk[chunk.length - 1].date }
    for (const key of Object.keys(chunk[0])) {
      if (key === 'date') continue
      bucket[key] = chunk.reduce((sum, row) => sum + Number(row[key] || 0), 0)
    }
    buckets.push(bucket)
  }
  return buckets
}

// Pick a round gridline step and a top-of-scale that is a multiple of it.
// The y axis is a count, so the step never drops below 1 — and crucially the
// tick count is bounded, rather than being one line per unit of the largest
// value (a 5,000-payment day used to emit 5,001 overlapping gridlines).
export function chartScale (maxVal, targetTicks = 4) {
  const peak = Math.max(Number(maxVal) || 0, 1)
  const rough = peak / targetTicks
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const candidates = [1, 2, 5, 10].map((m) => m * magnitude)
  const step = Math.max(1, Math.round(candidates.find((s) => s >= rough) ?? magnitude * 10))
  const top = Math.ceil(peak / step) * step

  const ticks = []
  for (let value = 0; value <= top; value += step) ticks.push(value)
  return { top, ticks }
}

export function formatChartDay (isoDate, locale) {
  const d = new Date(`${isoDate}T12:00:00Z`)
  return d.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' })
}

export function formatChartAxisDay (isoDate, locale) {
  const d = new Date(`${isoDate}T12:00:00Z`)
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
}

// Show the first and last label always, every label while the range is short,
// and otherwise thin out to roughly `targetLabels` evenly spaced dates.
export function shouldShowAxisLabel (index, total, targetLabels = 8) {
  if (index === 0 || index === total - 1) return true
  if (total <= 14) return true
  return index % Math.ceil(total / targetLabels) === 0
}

function pluralize (count, singular, plural) {
  return count === 1 ? singular : (plural ?? `${singular}s`)
}

/**
 * Render a grouped bar chart of daily counts into `chartEl`.
 *
 * @param {HTMLElement} chartEl        wrapper element; receives the SVG + aria-label
 * @param {Array<object>} rawDays      rows of `{ date, <seriesKey>: number }`
 * @param {object}   options
 * @param {Array}    options.series    `{ key, modifier, singular, plural? }` per bar
 * @param {string}   options.emptyText copy shown when there is nothing to plot
 * @param {Function} options.ariaLabel `(totals, days) => string`
 * @param {number}  [options.width]    fixed canvas width; omit to size by day count
 */
export function renderActivityChart (chartEl, rawDays, {
  series,
  emptyText,
  ariaLabel,
  locale,
  width: fixedWidth,
  height = 220,
  padLeft = 36,
  padRight = 12,
  padTop = 16,
  padBottom = 44,
  groupMinWidth = 32,
  barGap = 3,
  maxBarWidth = 12,
  labelOffset = 12,
  targetLabels = 8,
  maxPoints = 60
} = {}) {
  const days = resampleSeries(rawDays || [], maxPoints)

  if (!days.length) {
    chartEl.textContent = emptyText
    chartEl.className = 'activity-chart-wrap activity-chart-wrap--empty'
    chartEl.removeAttribute('aria-label')
    return days
  }

  chartEl.className = 'activity-chart-wrap'

  const valueOf = (day, key) => Number(day[key] || 0)
  const peak = Math.max(1, ...days.flatMap((day) => series.map((s) => valueOf(day, s.key))))
  const { top, ticks } = chartScale(peak)

  const width = fixedWidth ?? (padLeft + padRight + days.length * groupMinWidth)
  const chartWidth = width - padLeft - padRight
  const chartHeight = height - padTop - padBottom
  const groupWidth = chartWidth / days.length
  const barWidth = Math.min(
    maxBarWidth,
    (groupWidth - barGap * (series.length - 1) - 8) / series.length
  )
  const totalBarWidth = barWidth * series.length + barGap * (series.length - 1)

  const svgParts = [
    `<svg class="activity-chart" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" aria-hidden="true">`
  ]

  for (const tick of ticks) {
    const y = padTop + chartHeight - (tick / top) * chartHeight
    svgParts.push(`<line class="activity-chart__grid" x1="${padLeft}" y1="${y}" x2="${width - padRight}" y2="${y}" />`)
    svgParts.push(`<text class="activity-chart__tick" x="${padLeft - 8}" y="${y + 4}" text-anchor="end">${tick}</text>`)
  }

  days.forEach((day, index) => {
    const groupX = padLeft + index * groupWidth + groupWidth / 2
    const label = formatChartDay(day.date, locale)

    series.forEach((entry, seriesIndex) => {
      const value = valueOf(day, entry.key)
      const barHeight = (value / top) * chartHeight
      const x = groupX - totalBarWidth / 2 + seriesIndex * (barWidth + barGap)
      const y = padTop + chartHeight - barHeight

      svgParts.push(`<rect class="activity-chart__bar activity-chart__bar--${entry.modifier}" x="${x}" y="${y}" width="${barWidth}" height="${barHeight}" rx="2">
      <title>${label}: ${value} ${pluralize(value, entry.singular, entry.plural)}</title>
    </rect>`)
    })

    if (shouldShowAxisLabel(index, days.length, targetLabels)) {
      svgParts.push(`<text class="activity-chart__label" x="${groupX}" y="${height - labelOffset}" text-anchor="middle">${formatChartAxisDay(day.date, locale)}</text>`)
    }
  })

  svgParts.push('</svg>')

  const totals = Object.fromEntries(series.map((entry) => [
    entry.key,
    days.reduce((sum, day) => sum + valueOf(day, entry.key), 0)
  ]))

  chartEl.setAttribute('aria-label', ariaLabel(totals, days))
  chartEl.innerHTML = svgParts.join('')
  return days
}
